// src/hooks/useRevenueCat.js
import { useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { Purchases as PurchasesNative } from '@revenuecat/purchases-capacitor';
import { Purchases as PurchasesWeb }    from '@revenuecat/purchases-js';
import { useAuth }         from '../contexts/authContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import apiClient           from '../utils/apiClient';

export function useRevenueCat() {
  const { userId, isLoggedIn, isAuthReady } = useAuth();  
  const {
    setIsSubscribed,
    setOfferings,
    setIsRcReady
  } = useSubscription();

    // ref for previous isLoggedIn
    const prevLoggedIn = useRef(isLoggedIn);

  useEffect(() => {
    // 1) don’t do *anything* until your auth context has finished 
    //    hydrating token + userId from storage:
    if (!isAuthReady) {
      return;
    }


    // 2) did we just go from logged-in → logged-out?
    if (prevLoggedIn.current && !isLoggedIn) {
      console.log("[RC]: Logging out");
      const platform = Capacitor.getPlatform();
      const isNative = platform === 'ios' || platform === 'android';

      // RevenueCat logout
      if (isNative) {
        console.log("[RC]: purchasesNative.logOut();");
        PurchasesNative.logOut();
      } else {
        console.log("[RC]: purchasesWeb change user;");
        const anon = PurchasesWeb.generateRevenueCatAnonymousAppUserId();
        PurchasesWeb.getSharedInstance().changeUser(anon);
      }

      // reset subscription state
      setIsSubscribed(false);
      setOfferings(null);
      setIsRcReady(true);

      // update the ref and bail
      prevLoggedIn.current = isLoggedIn;
      return;
    }

    // 3) if we’re still not logged in (i.e. initial load as anon), do nothing
    if (!isLoggedIn || !userId) {
        setIsRcReady(true);
        prevLoggedIn.current = isLoggedIn;
        return;
    }

    // 4) otherwise we have a real userId → sync RC
    let removeListener;
    (async () => {
      try {
        const platform = Capacitor.getPlatform();
        const isNative = platform === 'ios' || platform === 'android';

        let customerInfo;
        if (isNative) {
          // switch RC to our user
          const { customerInfo: info } = await PurchasesNative.logIn({ appUserID: userId });
          customerInfo = info;

          // subscribe to entitlements updates
          removeListener = PurchasesNative.addCustomerInfoUpdateListener(
            ({ customerInfo: updatedInfo }) => {
              const active = Boolean(updatedInfo.entitlements.active['sous_premium']);
              setIsSubscribed(active);
            }
          );
        } else {
          // web: same via changeUser
          const instance = PurchasesWeb.getSharedInstance();
          await instance.changeUser(userId);
          customerInfo = await instance.getCustomerInfo();
        }

        // set initial entitlement state
        const isActive = Boolean(customerInfo.entitlements.active['sous_premium']);
        setIsSubscribed(isActive);

        // fetch offerings
        const offerings = isNative
          ? await PurchasesNative.getOfferings()
          : await PurchasesWeb.getSharedInstance().getOfferings();
        setOfferings(offerings);

        // optional: notify your backend
        apiClient.post('/subscriptions/update', { active: isActive }).catch(console.error);
      } catch (err) {
        console.error('RevenueCat sync error', err);
        setIsSubscribed(false);
      } finally {
        setIsRcReady(true);
      }
    })();

    return () => {
            if (typeof removeListener === 'function') {
                  try {
                    removeListener();
                  } catch (e) {
                   console.warn('[RC] failed to remove listener', e);
                  }
                }
    };
  }, [
    isAuthReady,
    isLoggedIn,
    userId,
    setIsSubscribed,
    setOfferings,
    setIsRcReady
  ]);
}
